import React, { useEffect, useState } from 'react';
import {
  collection, query, where, onSnapshot, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mail, Phone, Clock, Loader2, CheckCircle2 } from 'lucide-react';

const T = {
  en: {
    title: 'Pending Call Requests', empty: 'No pending requests.',
    claim: 'Claim This Booking', claiming: 'Claiming...',
    alreadyClaimed: 'Someone already claimed this — sorry!',
    claimedToast: 'Booking claimed — reach out to the visitor.',
    error: 'Action failed',
  },
  fr: {
    title: 'Demandes de rendez-vous en attente', empty: 'Aucune demande en attente.',
    claim: 'Réclamer ce rendez-vous', claiming: 'Réclamation...',
    alreadyClaimed: 'Quelqu\'un l\'a déjà réclamé — désolé !',
    claimedToast: 'Rendez-vous réclamé — contactez le visiteur.',
    error: 'Échec de l\'action',
  },
};

interface PendingBooking {
  id: string;
  visitorName: string;
  visitorEmail: string;
  visitorPhone?: string;
  reason?: string;
  slotStart: any;
  slotEnd: any;
}

export function PendingBookingsPanel() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = T[language] || T.en;

  const [bookings, setBookings] = useState<PendingBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'callBookings'),
      where('status', '==', 'pending_claim'),
      where('eligibleStaffIds', 'array-contains', user.uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() } as PendingBooking)));
        setLoading(false);
      },
      (err) => {
        console.error('[PendingBookingsPanel] listener error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  const claim = async (bookingId: string) => {
    if (!user?.uid) return;
    setClaimingId(bookingId);
    try {
      const ref = doc(db, 'callBookings', bookingId);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists() || snap.data().status !== 'pending_claim') {
          throw new Error('ALREADY_CLAIMED');
        }
        tx.update(ref, {
          status: 'claimed',
          claimedBy: user.uid,
          claimedAt: serverTimestamp(),
        });
      });
    } catch (err: any) {
      if (err?.message === 'ALREADY_CLAIMED') {
        alert(t.alreadyClaimed);
      } else {
        console.error('[PendingBookingsPanel] claim failed:', err);
        alert(t.error);
      }
    } finally {
      setClaimingId(null);
    }
  };

  const formatSlot = (ts: any) => {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-white flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-400" /> {t.title}
        {bookings.length > 0 && (
          <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-300">{bookings.length}</Badge>
        )}
      </h2>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-white/30" />
      ) : bookings.length === 0 ? (
        <p className="text-xs text-gray-500">{t.empty}</p>
      ) : (
        <ScrollArea className="max-h-96">
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className="bg-white/5 rounded-xl p-3 border border-white/10 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{b.visitorName}</span>
                  <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-300">
                    {formatSlot(b.slotStart)}
                  </Badge>
                </div>
                <div className="space-y-0.5 text-xs text-gray-400">
                  <p className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{b.visitorEmail}</p>
                  {b.visitorPhone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{b.visitorPhone}</p>}
                  {b.reason && <p className="text-gray-500 italic">{b.reason}</p>}
                </div>
                <Button
                  size="sm"
                  disabled={claimingId === b.id}
                  onClick={() => claim(b.id)}
                  className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                >
                  {claimingId === b.id ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {claimingId === b.id ? t.claiming : t.claim}
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}